"use client"

import { useCallback, useEffect } from "react"

import { useMutation, useQueryClient, type DefaultError, type MutateOptions, type UseMutationOptions, type UseMutationResult } from "@tanstack/react-query"

import { attachRequestContext } from "@/platform/observability/errors"
import { currentViewer } from "@/platform/session/current-viewer"
import { useViewer } from "@/platform/session/use-viewer"
import { ANONYMOUS_VIEWER } from "@/platform/session/viewer"
import { scopeToViewer, SupersededViewerError, type ViewerScope } from "@/platform/session/viewer-boundary"

export { SupersededViewerError }

interface Invocation<TVariables> {
  variables: TVariables
  scope: ViewerScope | null
}

const superseded = (error: unknown) => error instanceof SupersededViewerError

type Handlers<TData, TError extends Error, TVariables, TContext> = Pick<MutateOptions<TData, TError, TVariables, TContext>, "onSuccess" | "onError" | "onSettled">

const forInvocation = <TData, TError extends Error, TVariables, TContext>(
  handlers: Handlers<TData, TError, TVariables, TContext>
): Required<Handlers<TData, TError, Invocation<TVariables>, TContext>> => ({
  onSuccess: (data, { variables }, context) => handlers.onSuccess?.(data, variables, context),
  onError: (error, { variables }, context) => (superseded(error) ? undefined : handlers.onError?.(error, variables, context)),
  onSettled: (data, error, { variables }, context) => (superseded(error) ? undefined : handlers.onSettled?.(data, error, variables, context)),
})

export const usePrivateMutation = <TData = unknown, TError extends Error = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  allowAnonymous = false
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const queryClient = useQueryClient()
  const viewer = useViewer()
  const allowed = viewer !== null && (allowAnonymous || viewer !== ANONYMOUS_VIEWER)

  const result = useMutation<TData, TError, Invocation<TVariables>, TContext>({
    ...options,
    retry: (count, error) => {
      if (superseded(error)) return false
      const retry = options.retry ?? queryClient.getDefaultOptions().mutations?.retry ?? false
      return typeof retry === "function" ? retry(count, error) : retry === true || (typeof retry === "number" && count < retry)
    },
    mutationFn: async ({ variables, scope }) => {
      if (!scope?.isCurrent()) throw new SupersededViewerError()
      if (!options.mutationFn) throw new Error("Private session mutation requires a mutation function")
      const data = await options.mutationFn(variables).catch((error: unknown) => {
        attachRequestContext(error, { viewer: scope.viewer })
        throw scope.isCurrent() ? error : new SupersededViewerError(error)
      })
      if (!scope.isCurrent()) throw new SupersededViewerError()
      return data
    },
    onMutate: ({ variables, scope }) => (scope?.isCurrent() ? options.onMutate?.(variables) : undefined),
    ...forInvocation(options),
  })

  const { reset, mutate, mutateAsync } = result
  useEffect(() => {
    reset()
  }, [viewer, reset])

  const invocation = useCallback(
    (variables: TVariables): Invocation<TVariables> | null => {
      if (viewer === null || currentViewer(queryClient) !== viewer) return null
      return { variables, scope: allowed ? scopeToViewer(queryClient, viewer) : null }
    },
    [viewer, allowed, queryClient]
  )
  const guardedMutate = useCallback(
    (variables: TVariables, callOptions?: MutateOptions<TData, TError, TVariables, TContext>) => {
      const invoked = invocation(variables)
      if (invoked) mutate(invoked, callOptions && forInvocation(callOptions))
    },
    [invocation, mutate]
  )
  const guardedMutateAsync = useCallback(
    (variables: TVariables, callOptions?: MutateOptions<TData, TError, TVariables, TContext>) => {
      const invoked = invocation(variables)
      return invoked ? mutateAsync(invoked, callOptions && forInvocation(callOptions)) : Promise.reject(new SupersededViewerError())
    },
    [invocation, mutateAsync]
  )

  return { ...result, variables: result.variables?.variables, mutate: guardedMutate, mutateAsync: guardedMutateAsync } as unknown as UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >
}
